import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  Pressable,
  ViewToken,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, BookCard } from '../../components/ui';
import { BookService } from '../../services/bookService';
import { FireBook } from '../../models/FirestoreModels';
import { getShortTitle, getShortDescription } from '../../utils/bookHelpers';
import { CATEGORY_BOOK_IDS } from '../../data/categoryBookIds';

interface BookItem {
  id: string;
  book?: FireBook; // undefined = not loaded yet
  loading: boolean;
}

interface CategoryScreenParams {
  categoryId: string;
  categoryName: string;
}

const CategoryScreen: React.FC = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { categoryId, categoryName } = (route.params as CategoryScreenParams) || {};
  
  const [bookItems, setBookItems] = useState<BookItem[]>([]);
  const [initializing, setInitializing] = useState(true);
  const loadingQueue = useRef<Set<string>>(new Set());

  useEffect(() => {
    initializeBookList();
  }, [categoryId]);

  const initializeBookList = () => {
    // Normalize category ID (remove spaces, dashes, lowercase)
    const normalizedCategory = categoryId.toLowerCase().replace(/[\s_-]/g, '');
    
    // Get book IDs from hardcoded mapping
    const bookIds = CATEGORY_BOOK_IDS[normalizedCategory] || [];
    
    if (bookIds.length === 0) {
      console.warn(`⚠️ No books found for category: ${categoryId} (normalized: ${normalizedCategory})`);
      console.log('Available categories:', Object.keys(CATEGORY_BOOK_IDS));
      setBookItems([]);
      setInitializing(false);
      return;
    }
    
    console.log(`📚 Initializing ${bookIds.length} book placeholders for: ${categoryId}`);
    
    // Create placeholder items immediately (instant display)
    const placeholders: BookItem[] = bookIds.map(id => ({
      id,
      book: undefined,
      loading: false,
    }));
    
    setBookItems(placeholders);
    setInitializing(false);
  };

  const loadBooksInBatch = useCallback(async (bookIds: string[]) => {
    if (bookIds.length === 0) return;
    
    // Filter out books already being loaded
    const idsToLoad = bookIds.filter(id => !loadingQueue.current.has(id));
    
    if (idsToLoad.length === 0) return;
    
    // Mark all as in loading queue
    idsToLoad.forEach(id => loadingQueue.current.add(id));
    
    // Mark as loading in UI
    setBookItems(prev => prev.map(item => 
      idsToLoad.includes(item.id) ? { ...item, loading: true } : item
    ));
    
    console.log(`📦 Loading ${idsToLoad.length} books (parallel, progressive UI)...`);
    
    // Use the batch service but show results progressively
    // Still benefits from cache checking, but we could make it more progressive
    try {
      const result = await BookService.getBooksById(idsToLoad, 'en');
      
      if (result.success && result.data) {
        // Build map and update UI
        const booksMap = new Map(
          result.data.map(book => {
            const cleanId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
            return [cleanId, book];
          })
        );
        
        setBookItems(prev => prev.map(item => {
          const book = booksMap.get(item.id);
          return book 
            ? { ...item, book, loading: false }
            : idsToLoad.includes(item.id) ? { ...item, loading: false } : item;
        }));
        
        console.log(`✅ Loaded ${result.data.length} books`);
      } else {
        console.error('Failed to load books:', result.error);
        setBookItems(prev => prev.map(item => 
          idsToLoad.includes(item.id) ? { ...item, loading: false } : item
        ));
      }
    } catch (error) {
      console.error('Error batch loading books:', error);
      setBookItems(prev => prev.map(item => 
        idsToLoad.includes(item.id) ? { ...item, loading: false } : item
      ));
    } finally {
      idsToLoad.forEach(id => loadingQueue.current.delete(id));
    }
  }, []);

  const onViewableItemsChanged = useCallback(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    // Collect all visible book IDs that need loading
    const idsToLoad = viewableItems
      .map(({ item }) => item as BookItem)
      .filter(bookItem => !bookItem.book && !bookItem.loading)
      .map(bookItem => bookItem.id);
    
    // Load all visible books in one batch
    if (idsToLoad.length > 0) {
      loadBooksInBatch(idsToLoad);
    }
  }, [loadBooksInBatch]);

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 10, // Load when 10% visible
    minimumViewTime: 100, // Wait 100ms before triggering
  }).current;

  const handleBookPress = (bookItem: BookItem) => {
    if (!bookItem.book) return; // Don't navigate if not loaded
    
    const bookId = bookItem.book.id.includes('_') 
      ? bookItem.book.id.split('_')[0] 
      : bookItem.book.id;
    
    // @ts-ignore - Navigation types
    navigation.navigate('BookDetail', { 
      bookId, 
      languageCode: bookItem.book.languageCode || 'en'
    });
  };

  const renderBookItem = ({ item }: { item: BookItem }) => {
    if (!item.book) {
      // Show skeleton/placeholder while loading
      return (
        <View style={[styles.bookCard, styles.placeholderCard]}>
          <View style={styles.placeholderCover} />
          <View style={styles.placeholderContent}>
            <View style={styles.placeholderTitle} />
            <View style={styles.placeholderAuthor} />
            <View style={styles.placeholderDescription} />
          </View>
        </View>
      );
    }

    const book = item.book;
    const shortTitle = getShortTitle(book.title);
    const description = getShortDescription(book.introduction, 100);
    
    return (
      <BookCard
        key={book.id}
        title={shortTitle}
        author={book.author}
        coverUrl={book.coverImageUrl || book.coverImageFullUrl}
        insightsCount={book.insightIds?.length || book.statistics?.insights}
        mentorNote={description}
        variant="compact"
        onPress={() => handleBookPress(item)}
        style={styles.bookCard}
      />
    );
  };

  if (initializing) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        <Text style={styles.headerTitle}>{categoryName}</Text>
        <View style={styles.headerSpacer} />
      </View>

      {bookItems.length > 0 ? (
        <FlatList
          data={bookItems}
          renderItem={renderBookItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          initialNumToRender={10}
          maxToRenderPerBatch={5}
          windowSize={5}
          removeClippedSubviews={true}
        />
      ) : (
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>No books found</Text>
          <Text style={styles.emptyDescription}>
            We couldn't find any books in this category yet.
          </Text>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  listContent: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.xl,
    paddingBottom: Theme.spacing.xxxl,
  },
  bookCard: {
    marginBottom: Theme.spacing.sm,
  },
  placeholderCard: {
    flexDirection: 'row',
    padding: Theme.spacing.md,
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.md,
    height: 120,
  },
  placeholderCover: {
    width: 70,
    height: 90,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: Theme.borderRadius.sm,
    marginRight: Theme.spacing.md,
  },
  placeholderContent: {
    flex: 1,
    justifyContent: 'center',
  },
  placeholderTitle: {
    height: 18,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: 4,
    marginBottom: Theme.spacing.xs,
    width: '80%',
  },
  placeholderAuthor: {
    height: 14,
    backgroundColor: 'rgba(0,0,0,0.06)',
    borderRadius: 4,
    marginBottom: Theme.spacing.sm,
    width: '50%',
  },
  placeholderDescription: {
    height: 12,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 4,
    width: '90%',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Theme.spacing.xxxxl,
  },
  emptyTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    marginBottom: Theme.spacing.sm,
  },
  emptyDescription: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
  },
});

export default CategoryScreen;

